import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../../webmodule-common/other/ui/modal-confirmation';
import { cache } from '../../cache-impl/cache-registry';
import { customElement } from 'lit/decorators.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { EventGetReference, EventSnippet } from '../../../../webmodule-common/other/ui/events';
import { FabricationPurchaseOrderState, PurchaseOrderSummaryView } from '../../../api/dealer-api-interface-all';
import { getApiFactory } from '../../../api/api-injector';
import {
  getPurchaseOrderLink,
  getPurchaseOrderNumberFormatted,
  getPurchaseOrderStatusBadge
} from '../data/fabrication-purchase-order-helper-functions';
import { LangFabricationPurchaseOrder } from '../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import { html, TemplateResult } from 'lit';
import { DataTableWrapper } from '../../../../webmodule-common/other/ui/datatable-view';
import { RequestPage, ResultPaginated } from '../../../../webmodule-common/other/ui/RequestPage';
import {
  WebModuleTable,
  WebModuleTableColumnDef,
  WebmoduleSelectEvent
} from '../../../../webmodule-common/components/src/webmodule-components';

export type TableEventFabricationPurchaseOrder = (po: PurchaseOrderSummaryView) => Promise<void>;

export interface FabricationPurchaseOrderSummaryTableOptions {
  states: FabricationPurchaseOrderState[];
  title: EventSnippet;
  branchId: EventGetReference;
  pageFragment: string;
  cancelEvent?: TableEventFabricationPurchaseOrder;
}

@customElement('wm-fabricationpurchaseordersummarytable')
export class FabricationPurchaseOrderSummaryTable extends DataTableWrapper<PurchaseOrderSummaryView> {
  eventTitle: EventSnippet;
  branchId: EventGetReference;
  states: FabricationPurchaseOrderState[];
  searchTerm: string | null = null;
  pageFragment: string;
  dataIsStale = false;
  cancelEvent?: TableEventFabricationPurchaseOrder;
  private userCache = cache().userProfile;

  constructor(options: FabricationPurchaseOrderSummaryTableOptions) {
    super();
    this.states = options.states;
    this.eventTitle = options.title;
    this.branchId = options.branchId;
    this.pageFragment = options.pageFragment;
    this.cancelEvent = options.cancelEvent;
  }

  isDataLoadDelayed(): boolean {
    return true;
  }

  override enableFiltering(): boolean {
    return true;
  }

  // The list tabs are a fixed-height region; let the table own the vertical scroll so a
  // second (tab) scrollbar does not appear beside it. The fill-height flex chain for the
  // wrapper is set up in tab-manager.styles.ts, keyed on the marker class DataTableWrapper
  // adds. Picker subclasses that render this table in a modal turn it back off.
  override fillHeight(): boolean {
    return true;
  }

  override updateFilter(_searchTerm: string | null): void {
    this.searchTerm = _searchTerm;
  }

  excludeLineSetLocked(): boolean {
    return false;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<PurchaseOrderSummaryView>> {
    const api = getApiFactory().fabricationPurchaseOrder();
    const result = await api.getPurchaseOrderSummary({
      branchId: await this.branchId(),
      states: this.states,
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      searchTerm: this.searchTerm ?? null,
      excludeLineSetLocked: this.excludeLineSetLocked()
    });

    if (!result) {
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };
    }

    await this.userCache.preFetch(result.results.map(x => x.assignedToUserId));

    return result;
  }

  getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from Bootstrap `colpx-*` / `colpxmax-*` classes (which
    // don't pierce the new table's shadow DOM) to the column's own width model.
    // A lone `minWidth` leaves the cell at `flex: 1 1 0`, so every column would grow
    // to an equal share; the short, bounded columns therefore carry a `maxWidth` too
    // and only the free-text Title column is left uncapped to absorb the slack.
    //
    // No column is `sortable`, deliberately: `RequestGetPurchaseOrderSummary` has no sort
    // field at all (branchId / states / pageIndex / pageSize / searchTerm /
    // excludeLineSetLocked), so getRowsFromServer has nothing to forward, and the table does
    // not sort client-side - it only re-emits sortField on the fetch event. A sortable header
    // here would re-fetch and return the identical order. Making these lists sortable needs a
    // sort parameter on the summary endpoint first.
    return [
      {
        title: () => LangFabricationPurchaseOrder.numberColumn(),
        fieldName: 'purchaseOrderNumber',
        minWidth: '70px',
        maxWidth: '140px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          return getPurchaseOrderLink(po, getPurchaseOrderNumberFormatted(po));
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.titleColumn(),
        fieldName: 'title',
        minWidth: '150px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          return html`${po.title}`;
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.stateColumn(),
        fieldName: 'state',
        minWidth: '90px',
        maxWidth: '180px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          return getPurchaseOrderStatusBadge(po.state);
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.itemsColumn(),
        fieldName: 'itemCount',
        minWidth: '70px',
        maxWidth: '110px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          return html`${po.itemCount}`;
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.linkedJobsColumn(),
        fieldName: 'linkedJobCount',
        minWidth: '70px',
        maxWidth: '130px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          return html`${po.linkedJobCount}`;
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.authorColumn(),
        fieldName: 'assignedToUserId',
        minWidth: '120px',
        maxWidth: '200px',
        fade: true,
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          return this.getUserDisplayValue(po.assignedToUserId);
        }
      },
      {
        title: () => LangFabricationPurchaseOrder.createdColumn(),
        fieldName: 'dateCreated',
        minWidth: '110px',
        maxWidth: '140px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          const dt = new Date(po.dateCreated);
          return html`${dt.toLocaleDateString()}`;
        }
      },
      {
        title: () => '',
        width: '64px',
        fieldName: 'xx',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const po = item as PurchaseOrderSummaryView;
          return this.ellipsisMenu(po);
        }
      }
    ];
  }

  getDefaultSortAsc(): boolean {
    return false;
  }

  getDefaultSortField(): string {
    return 'purchaseOrderNumber';
  }

  protected getUserDisplayValue(value: string): TemplateResult {
    if (value === emptyGuid) return html``;
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue ?? ''}`;
  }

  private async onMenuItemSelected(event: WebmoduleSelectEvent, po: PurchaseOrderSummaryView) {
    if (event.detail.item.value === 'cancel') {
      // A Draft order carrying a quote common id has already reached the supplier: an issue that
      // failed after the V6 quote was created reverts to Draft but keeps the key. Cancelling is
      // dealer-side only, so the supplier is left holding an order nobody has withdrawn - say so
      // rather than reusing the plain "cannot be undone" wording.
      const message =
        po.quoteCommonId != null
          ? LangFabricationPurchaseOrder.cancelConfirmSupplierMayHold(po.purchaseOrderNumber)
          : LangFabricationPurchaseOrder.cancelConfirm();

      if (await AskConfirmation(message, getConfirmationButtons()[ConfirmationButtonType.yesNo])) {
        await this.cancelEvent?.(po);
      }
    }
  }

  private ellipsisMenu(po: PurchaseOrderSummaryView): TemplateResult {
    if (po.state !== FabricationPurchaseOrderState.Draft) return html``;

    return html`<webmodule-dropdown
      placement="bottom-end"
      hoist
      @webmodule-select=${(e: WebmoduleSelectEvent) => this.onMenuItemSelected(e, po)}
    >
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        <webmodule-menu-item value="cancel">
          <webmodule-icon slot="prefix" library="fa" name="fas-ban"></webmodule-icon>
          ${LangFabricationPurchaseOrder.cancelAction()}
        </webmodule-menu-item>
      </webmodule-menu>
    </webmodule-dropdown>`;
  }
}
